import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { BellRinging, DeviceMobile, Globe, Check, Warning, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Compact push-notification diagnostics card for the Account & Sessions
 * page. Shows web-push VAPID status, native-push Firebase status, and a
 * count of registered subscriptions across all this user's devices.
 * Also lets the user fire a test push (routes to whichever dispatchers
 * are configured — web-push, FCM, APNs).
 */
export default function PushDiagnosticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/push/diagnostics");
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/push/test");
      setTestResult({ ok: true, data });
    } catch (e) {
      setTestResult({ ok: false, error: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setTesting(false); }
  };

  if (loading && !data) {
    return (
      <section className="sw-card p-6" data-testid="push-diagnostics-loading">
        <div className="text-sm text-slate-500">Loading push status…</div>
      </section>
    );
  }
  if (!data) return null;

  const webOk = !!data.web_push?.configured;
  const nativeOk = !!(data.native_push?.configured && data.native_push?.initialized);
  const counts = data.subscriptions || {};

  return (
    <section className="sw-card overflow-hidden" data-testid="push-diagnostics-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-brand-cyan/15 text-brand-cyan flex items-center justify-center">
            <BellRinging size={18} weight="duotone" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white tracking-tight">Push notifications</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Delivered via VAPID (browser) + Firebase FCM/APNs (iOS + Android).
            </p>
          </div>
        </div>
        <button onClick={sendTest} disabled={testing}
          data-testid="push-test-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] border border-brand-cyan/40 bg-brand-cyan/5 text-brand-cyan hover:bg-brand-cyan/15 rounded transition-colors disabled:opacity-50">
          <PaperPlaneTilt size={11} weight="bold" />
          {testing ? "Sending…" : "Send test"}
        </button>
      </div>

      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ChannelRow
          testid="push-channel-web"
          icon={<Globe size={16} weight="duotone" className="text-brand-cyan" />}
          title="Web Push (VAPID)"
          ok={webOk}
          okLabel="Configured"
          notOkLabel="Not configured"
          detail={webOk
            ? "Browser subscribers receive notifications via the service worker."
            : "Set VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY in the backend .env to enable."}
          count={counts.web ?? 0}
          countLabel="browser devices"
        />
        <ChannelRow
          testid="push-channel-native"
          icon={<DeviceMobile size={16} weight="duotone" className="text-brand-cyan" />}
          title="Native (FCM + APNs)"
          ok={nativeOk}
          okLabel="Firebase active"
          notOkLabel="Firebase not configured"
          detail={nativeOk
            ? "iOS + Android subscribers dispatched through Firebase Admin SDK."
            : data.native_push?.note || data.native_push?.error || "Set FIREBASE_CREDENTIALS_JSON in the backend .env to enable."}
          count={(counts.ios ?? 0) + (counts.android ?? 0)}
          countLabel={`native devices (${counts.ios ?? 0} iOS · ${counts.android ?? 0} Android)`}
        />
      </div>

      {testResult && (
        <div className="px-6 pb-4 -mt-2">
          <div className={`text-[12px] px-3 py-2 rounded border ${testResult.ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : "border-red-500/40 bg-red-500/10 text-red-200"}`}
            data-testid="push-test-result">
            {testResult.ok ? (
              <span>
                Dispatched to {testResult.data?.total ?? 0} device
                {testResult.data?.total === 1 ? "" : "s"} —
                {" "}{testResult.data?.successful ?? 0} succeeded,
                {" "}{testResult.data?.failed ?? 0} failed,
                {" "}{testResult.data?.removed ?? 0} removed (expired).
                {testResult.data?.native?.skipped_reason && (
                  <> Native skipped: {testResult.data.native.skipped_reason}.</>
                )}
              </span>
            ) : (
              <span>Test failed: {testResult.error}</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ChannelRow({ testid, icon, title, ok, okLabel, notOkLabel, detail, count, countLabel }) {
  return (
    <div className="rounded-md border border-border p-3.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <div className="text-white text-sm font-medium">{title}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${
          ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-slate-500/40 bg-slate-500/10 text-slate-400"
        }`}>
          {ok ? <Check size={9} weight="bold" /> : <Warning size={9} weight="bold" />}
          {ok ? okLabel : notOkLabel}
        </span>
      </div>
      <div className="mt-2 text-[12px] text-slate-400 leading-snug">{detail}</div>
      <div className="mt-2 text-[11px] text-slate-500">
        <span className="text-white font-semibold">{count}</span> {countLabel}
      </div>
    </div>
  );
}
